import { useMemo, useState } from 'react';

import { Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Form } from '@opendoor/atoms/form';
import { Text } from '@opendoor/atoms/text';
import {
  ACCOUNT_FORM_SMS_CONSENT,
  normalizePhoneNumber,
  PHONE_FIELD_NAME,
  PhoneConsentFields,
  phoneNumberSchema,
  useSmsConsentState,
} from '@opendoor/auth-fe/forms';
import { IconDoor } from '@tabler/icons-react';
import { useForm } from '@tanstack/react-form';
import Link from 'next/link';
import { z } from 'zod';

import type {
  TourAppointmentFieldsFragment,
  TourAvailabilityQuery,
} from './__generated__/tours.zeus';
import { AppStoreBadges } from './AppStoreBadges';
import { TOUR_SHEET_COPY } from './tourBookingSheetCopy';
import type { BookTourFailureReason } from './tourErrors';
import type { BookTourResult } from './useBookTour';

type AvailabilityDay = TourAvailabilityQuery['tours']['availability']['days'][number];
type AvailabilitySlot = AvailabilityDay['slots'][number];

export type ChosenSlot = {
  appointmentDate: string;
  appointmentTime: string;
  durationMinutes: number;
  /** Slot start rendered for the buyer, e.g. "10:00 AM - 10:30 AM". */
  label: string;
  /** Set for a slot restored from the login-return URL, before availability reconciliation. */
  fromReplay?: boolean;
};

// Availability DateTimes are UTC instants; the buyer and the booking input both
// need the listing's wall clock, which only Intl can produce from an instant.
export const zonedParts = (
  dateTime: string,
  timezone: string | null,
): { date: string; hour: number; minute: string; second: string } => {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone: timezone ?? 'UTC',
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
      .formatToParts(new Date(dateTime))
      .filter((part) => part.type !== 'literal')
      .map((part) => [part.type, part.value]),
  );
  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    hour: Number(parts.hour),
    minute: parts.minute,
    second: parts.second,
  };
};

const formatTime = (dateTime: string, timezone: string | null): string => {
  const { hour, minute } = zonedParts(dateTime, timezone);
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return `${display}:${minute} ${suffix}`;
};

const slotLabel = (start: string, end: string, timezone: string | null): string =>
  `${formatTime(start, timezone)} - ${formatTime(end, timezone)}`;

const timezoneAbbreviation = (timezone: string, at: Date): string | null => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    }).formatToParts(at);
    return parts.find((part) => part.type === 'timeZoneName')?.value ?? null;
  } catch {
    return null;
  }
};

const timezoneLabel = (timezone: string | null, slot: AvailabilitySlot | undefined): string => {
  if (!timezone) {
    return '';
  }
  const abbreviation = timezoneAbbreviation(timezone, slot ? new Date(slot.startTime) : new Date());
  return abbreviation ? ` (${abbreviation})` : '';
};

export const toBookingInput = (slot: AvailabilitySlot, timezone: string | null): ChosenSlot => {
  // Rails joins these and parses in the listing timezone
  // (viewing_appointments_controller#scheduled_for), so the input must be the
  // listing-local wall clock, not the UTC instant the API returns.
  const start = zonedParts(slot.startTime, timezone);
  return {
    appointmentDate: start.date,
    appointmentTime: `T${String(start.hour).padStart(2, '0')}:${start.minute}:${start.second}`,
    durationMinutes: 30,
    label: slotLabel(slot.startTime, slot.endTime, timezone),
  };
};

// Mirrors the iOS TimeSlotGrid: three columns, grouped by time of day.
const timeOfDayGroup = (hour: number): string => {
  if (hour < 12) {
    return 'Morning';
  }
  if (hour < 17) {
    return 'Afternoon';
  }
  return 'Evening';
};

const slotGroups = (
  slots: AvailabilitySlot[],
  timezone: string | null,
): [string, AvailabilitySlot[]][] => {
  const groups: Record<string, AvailabilitySlot[]> = { Morning: [], Afternoon: [], Evening: [] };
  for (const slot of slots) {
    groups[timeOfDayGroup(zonedParts(slot.startTime, timezone).hour)].push(slot);
  }
  return Object.entries(groups).filter(([, slots]) => slots.length > 0);
};

export const SlotPickerStep = ({
  days,
  availableNow,
  timezone,
  isLoading,
  onChoose,
  onTourNow,
  onRetry,
  nextAvailable,
  notice,
}: {
  days: AvailabilityDay[];
  availableNow: boolean;
  nextAvailable: AvailabilityDay['slots'][number] | null;
  timezone: string | null;
  isLoading: boolean;
  onChoose: (slot: ChosenSlot) => void;
  onTourNow: () => void;
  onRetry: () => void;
  /** Why the buyer landed back on the picker after a failed booking, if they did. */
  notice: string | null;
}) => {
  const [selectedDate, setSelectedDate] = useState<string | null>(days[0]?.date ?? null);
  const [selectedSlot, setSelectedSlot] = useState<ChosenSlot | null>(null);
  const selectedDay = days.find((day) => day.date === selectedDate) ?? days[0];

  if (isLoading) {
    return (
      <Col className="min-h-60 items-center justify-center gap-3">
        <Text as="span" className="text-body-md text-od-content-secondary">
          Loading tour times...
        </Text>
      </Col>
    );
  }

  // A missing listing timezone would render slot labels and booking inputs in
  // UTC; without it the availability data cannot be shown safely.
  if (days.length > 0 && !timezone) {
    return (
      <Col className="gap-3">
        <Text as="span" className="text-body-md text-od-content-secondary">
          We couldn’t load tour times. Please try again.
        </Text>
        <Button
          variant="secondary"
          onClick={onRetry}
          analyticsName="tours-availability-retry"
          aria-label="Retry loading tour times"
        >
          Retry
        </Button>
      </Col>
    );
  }

  if (days.length === 0) {
    return (
      <Col className="gap-3">
        <Text as="span" className="text-body-md text-od-content-primary">
          {TOUR_SHEET_COPY.slot.emptyState}
        </Text>
        <Text as="span" className="text-body-md text-od-content-secondary">
          {TOUR_SHEET_COPY.slot.tourWithAgent}
        </Text>
      </Col>
    );
  }

  const chipLabel = (slot: AvailabilitySlot): string => formatTime(slot.startTime, timezone);

  return (
    <Col className="h-full gap-4">
      {notice && (
        <Text as="span" className="text-body-sm text-od-content-error">
          {notice}
        </Text>
      )}
      {availableNow && nextAvailable && (
        <Button
          variant="secondary"
          onClick={onTourNow}
          analyticsName="tours-tour-now"
          aria-label="Tour now - a 30-minute tour is available"
          className="w-full items-center gap-3 p-4 text-left"
        >
          <IconDoor size={20} className="text-od-content-secondary" />
          <Col className="gap-0.5">
            <Text as="span" className="text-body-md text-od-content-primary">
              {TOUR_SHEET_COPY.slot.tourNow}
            </Text>
            <Text as="span" className="text-body-sm text-od-content-secondary">
              30 min tour available
            </Text>
          </Col>
        </Button>
      )}
      <Text as="span" className="text-body-md text-od-content-primary">
        Select a date and time{timezoneLabel(timezone, selectedDay?.slots[0])}
      </Text>
      <div className="grid grid-cols-5 gap-2">
        {days.slice(0, 5).map((day) => (
          <Button
            key={day.date}
            variant={day.date === selectedDay?.date ? 'primary' : 'secondary'}
            onClick={() => {
              setSelectedDate(day.date);
              setSelectedSlot(null);
            }}
            analyticsName="tours-day-chip"
            aria-label={`Tour on ${day.date}`}
            className="h-auto w-full flex-col gap-0.5 p-2 text-center"
          >
            <Text
              as="span"
              className={`block text-body-sm ${
                day.date === selectedDay?.date ? 'text-od-content-inverse-primary' : ''
              }`}
            >
              {new Date(`${day.date}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short' })}
            </Text>
            <Text
              as="span"
              className={`block text-body-sm ${
                day.date === selectedDay?.date
                  ? 'text-od-content-inverse-primary'
                  : 'text-od-content-secondary'
              }`}
            >
              {new Date(`${day.date}T12:00:00`).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
              })}
            </Text>
          </Button>
        ))}
      </div>
      <Col className="min-h-0 flex-1 gap-4 overflow-y-auto">
        {slotGroups(selectedDay?.slots ?? [], timezone).map(([group, slots]) => (
          <Col className="gap-2" key={group}>
            <Text as="span" className="text-body-md text-od-content-primary">
              {group}
            </Text>
            <div className="grid grid-cols-3 gap-2">
              {slots.map((slot) => {
                const chosen = toBookingInput(slot, timezone);
                const isSelected =
                  selectedSlot?.appointmentDate === chosen.appointmentDate &&
                  selectedSlot?.appointmentTime === chosen.appointmentTime;
                return (
                  <Button
                    key={slot.startTime}
                    variant={isSelected ? 'primary' : 'secondary'}
                    onClick={() => setSelectedSlot(chosen)}
                    analyticsName="tours-slot-chip"
                    aria-label={chipLabel(slot)}
                    className="h-auto w-full p-2 text-center"
                  >
                    <Text
                      as="span"
                      className={`text-body-sm ${
                        isSelected ? 'text-od-content-inverse-primary' : ''
                      }`}
                    >
                      {chipLabel(slot)}
                    </Text>
                  </Button>
                );
              })}
            </div>
          </Col>
        ))}
      </Col>
      <Col as="footer" className="shrink-0 border-t border-od-border-primary pt-4 pb-0">
        <Button
          variant="primary"
          onClick={() => selectedSlot && onChoose(selectedSlot)}
          disabled={!selectedSlot}
          aria-label="Review tour"
          analyticsName="tours-slot-review"
          className="w-full"
        >
          Review tour
        </Button>
      </Col>
    </Col>
  );
};

export const LoginStep = ({
  slot,
  onContinue,
}: {
  slot: ChosenSlot | null;
  onContinue: (redirectTo: string) => void;
}) => {
  const handleClick = () => {
    const params = new URLSearchParams(window.location.search);
    if (slot) {
      // D7: the chosen slot rides through the login redirect and the sheet
      // replays it on return.
      params.set('tourDate', slot.appointmentDate);
      params.set('tourTime', slot.appointmentTime);
      params.set('tourDuration', String(slot.durationMinutes));
    }
    onContinue(
      `/login?redirectTo=${encodeURIComponent(
        `${window.location.pathname}${params.toString() ? `?${params}` : ''}`,
      )}`,
    );
  };
  return (
    <Col className="gap-4">
      <Text as="span" className="text-subheader-md text-od-content-primary">
        {TOUR_SHEET_COPY.login.heading}
      </Text>
      <Text as="p" className="text-body-md text-od-content-secondary">
        {TOUR_SHEET_COPY.login.body}
      </Text>
      <Button
        variant="primary"
        onClick={handleClick}
        aria-label="login continue"
        analyticsName="tours-login-continue"
      >
        {TOUR_SHEET_COPY.login.continue}
      </Button>
    </Col>
  );
};

export const PhoneNumberStep = ({
  isSaving,
  saveFailed,
  onSave,
}: {
  isSaving: boolean;
  saveFailed: boolean;
  onSave: (phoneNumber: string, marketingOptIn: boolean, consentLanguage: string) => void;
}) => {
  const smsConsentState = useSmsConsentState(ACCOUNT_FORM_SMS_CONSENT);
  const form = useForm({
    defaultValues: { [PHONE_FIELD_NAME]: '' },
    validators: { onChange: z.object({ [PHONE_FIELD_NAME]: phoneNumberSchema }) },
    onSubmit: async ({ value }) => {
      onSave(
        normalizePhoneNumber(value.phoneNumber),
        smsConsentState.optIn,
        smsConsentState.spec.version,
      );
    },
  });
  return (
    <Col className="gap-4">
      <Text as="span" className="text-subheader-md text-od-content-primary">
        {TOUR_SHEET_COPY.phone.heading}
      </Text>
      <Text as="p" className="text-body-md text-od-content-secondary">
        {TOUR_SHEET_COPY.phone.body}
      </Text>
      <Form.Root form={form} noValidate className="flex w-full flex-col gap-4">
        <PhoneConsentFields consent={smsConsentState} analyticsPrefix="tours-phone" />
        <Form.SubmitButton
          className="w-full"
          disabled={isSaving}
          analyticsName="tours-phone-save"
          aria-label="save phone number"
        >
          {TOUR_SHEET_COPY.phone.save}
        </Form.SubmitButton>
        {saveFailed && (
          <Text as="span" className="text-body-sm text-od-content-error">
            {TOUR_SHEET_COPY.phone.saveFailed}
          </Text>
        )}
      </Form.Root>
    </Col>
  );
};

export const bookTourFailureCopy = (result: BookTourResult | null): string | null => {
  if (!result) {
    return null;
  }
  if (result.kind !== 'failed') {
    return null;
  }
  const byReason: Record<BookTourFailureReason, string> = {
    slotUnavailable: TOUR_SHEET_COPY.review.slotTaken,
    invalidInput: TOUR_SHEET_COPY.review.outsideHours,
    unauthorized: TOUR_SHEET_COPY.review.unauthorized,
    unauthenticated: TOUR_SHEET_COPY.review.unauthorized,
    reauthenticationRequired: TOUR_SHEET_COPY.review.unauthorized,
    timeout: TOUR_SHEET_COPY.review.timeout,
    unknown: TOUR_SHEET_COPY.review.unknown,
  };
  return byReason[result.reason];
};

export const ReviewStep = ({
  slot,
  lastResult,
  isBooking,
  onConfirm,
}: {
  slot: ChosenSlot;
  lastResult: BookTourResult | null;
  isBooking: boolean;
  onConfirm: () => void;
}) => {
  const errorCopy = bookTourFailureCopy(lastResult);
  return (
    <Col className="gap-4">
      <Text as="span" className="text-subheader-md text-od-content-primary">
        {TOUR_SHEET_COPY.review.heading}
      </Text>
      <Text as="span" className="text-body-md text-od-content-primary">
        {new Date(`${slot.appointmentDate}T12:00:00`).toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        })}{' '}
        &middot; {slot.label}
      </Text>
      {errorCopy && (
        <Text as="span" className="text-body-sm text-od-content-error">
          {errorCopy}
        </Text>
      )}
      <Button
        variant="primary"
        onClick={onConfirm}
        disabled={isBooking}
        aria-label="review confirm"
        analyticsName="tours-review-confirm"
      >
        {TOUR_SHEET_COPY.review.confirm}
      </Button>
    </Col>
  );
};

// True UTC: the availability DateTimes carry the listing's offset, and
// appending Z to the stripped wall clock would shift the event by that offset.
const icsTimestamp = (dateTime: string): string =>
  new Date(dateTime).toISOString().replace(/[-:]/g, '').replace(/\.000/, '');

export const ConfirmationStep = ({
  appointment,
  addressToken,
}: {
  appointment: TourAppointmentFieldsFragment;
  /** Listing address token for app-handoff badge attribution, when known. */
  addressToken?: string | null;
}) => {
  const lines = appointment.scheduledFor
    ? [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//Opendoor//Self Tour//EN',
        'BEGIN:VEVENT',
        `DTSTART:${icsTimestamp(appointment.scheduledFor)}`,
        ...(appointment.endTime ? [`DTEND:${icsTimestamp(appointment.endTime)}`] : []),
        `SUMMARY:Self tour - ${appointment.readableAddress ?? 'Opendoor home'}`,
        ...(appointment.readableAddress ? [`LOCATION:${appointment.readableAddress}`] : []),
        'END:VEVENT',
        'END:VCALENDAR',
      ]
    : null;
  // iOS Safari navigates data: URLs instead of downloading them; a Blob URL
  // behaves as a download on the platforms buyers actually use.
  const calendarHref = useMemo((): string | null => {
    if (lines === null) {
      return null;
    }
    const body = lines.join('\r\n');
    if (typeof URL.createObjectURL === 'function') {
      return URL.createObjectURL(new Blob([body], { type: 'text/calendar' }));
    }
    return `data:text/calendar;charset=utf-8,${encodeURIComponent(body)}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointment.scheduledFor, appointment.endTime, appointment.readableAddress]);
  return (
    <Col className="gap-4">
      <Text as="span" className="text-subheader-md text-od-content-primary">
        {TOUR_SHEET_COPY.confirmation.heading}
      </Text>
      <Text as="p" className="text-body-md text-od-content-secondary">
        {TOUR_SHEET_COPY.confirmation.bodyPre}
        <Link href="/dashboard" className="text-od-content-primary underline">
          {TOUR_SHEET_COPY.confirmation.myTours}
        </Link>
        {TOUR_SHEET_COPY.confirmation.bodyPost}
      </Text>
      <Row className="gap-3">
        {calendarHref && (
          <a
            href={calendarHref}
            download="opendoor-self-tour.ics"
            className="text-od-content-primary underline"
          >
            {TOUR_SHEET_COPY.confirmation.addToCalendar}
          </a>
        )}
      </Row>
      <Text as="span" className="text-body-sm text-od-content-secondary">
        {TOUR_SHEET_COPY.confirmation.appHandoff}
      </Text>
      <AppStoreBadges addressToken={addressToken} />
    </Col>
  );
};

export const ContactSupportStep = () => (
  <Col className="gap-4">
    <Text as="span" className="text-subheader-md text-od-content-primary">
      {TOUR_SHEET_COPY.contactSupport.heading}
    </Text>
    <Text as="p" className="text-body-md text-od-content-secondary">
      {TOUR_SHEET_COPY.contactSupport.body}
    </Text>
  </Col>
);
